import { Injectable } from '@angular/core';
import { ComponentController } from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty } from 'lodash-es';
import { Observable, combineLatest, of } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';

import { NotificationType } from 'application/notification/model/notification.type';
import { NotificationApplicationPort } from 'application/notification/port/notification-application.port';
import { INotification } from 'domain/notification/notification';
import { OrganizationPropertyName } from 'organization/model/organization-property-name';
import { AddCoacheeEvent } from 'presentation/app-sidebar/sidebar-add-coachee/events/add-coachee.event';
import { AddExistingCoacheeEvents } from 'presentation/app-sidebar/sidebar-add-existing-coachee/events/add-existing-coachee.events';
import { UserRole } from 'user/model/user-role';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';

import { AppService } from '../app.service';
import { AppSidebarState, AppSidebarStore } from './app-sidebar.store';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AppSidebarController extends ComponentController<AppSidebarState> {
	public i18n = 'presentation.app-sidebar';

	public locale$: Observable<string>;
	public userId$: Observable<string>;
	public appChatUserId$: Observable<string>;
	public notifications$: Observable<INotification[]>;
	public isNotificationsExpanded$: Observable<boolean>;
	public notificationsIcon$: Observable<string>;
	public hasCoacheesWithoutCoach$: Observable<boolean>;
	public showCloseAllButton$: Observable<boolean>;
	public showCoachingPlan$: Observable<boolean>;
	public showCoacheesList$: Observable<boolean>;
	public coacheeName$: Observable<string>;
	public coacheeProfileRoute$: Observable<string[]>;

	constructor(
		store: AppSidebarStore,
		private _appService: AppService,
		private _translateService: TranslateService,
		private _userApplicationPort: UserApplicationPort,
		private _notificationApplicationPort: NotificationApplicationPort
	) {
		super(store);

		this._initObservables();
		this._initListeners();
	}

	public addCoachee(data: AddCoacheeEvent): Observable<void> {
		return this._userApplicationPort.observeSessionUserId().pipe(
			take(1),
			switchMap((userId) =>
				this._userApplicationPort.createUser({
					coachId: userId,
					...data,
					role: UserRole.COACHEE,
				})
			)
		);
	}

	public cancelEditUserPlan() {
		this.store.update({ selectedUserId: null });
		this._appService.setAppSidebarSelectedUserId(null);
	}

	public openUserPlan(userId: string) {
		this.store.update({ selectedUserId: userId });
	}

	public openChatWithUser(userId: string) {
		this._appService.setAppChatUserId(userId);
		this._appService.showChat();
	}

	public toggleSideBar() {
		this._appService.toggleSideBar();
	}

	public closeAllNotifications() {
		this._notificationApplicationPort.markAllNotificationsRead().subscribe();
	}

	public toggleNotifications() {
		this.store.update((state) => ({
			isNotificationsExpanded: !state.isNotificationsExpanded,
		}));
	}

	public addExistingCoachee(data: AddExistingCoacheeEvents): Observable<void> {
		return this._userApplicationPort.associateCoachWithCoachee(data);
	}

	private _initListeners() {
		this._notificationApplicationPort
			.fetchNotifications()
			.pipe(untilDestroyed(this))
			.subscribe();
	}

	private _initObservables() {
		this.isNotificationsExpanded$ = this.select('isNotificationsExpanded').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.notificationsIcon$ = this.isNotificationsExpanded$.pipe(
			map((isNotificationExpanded) =>
				isNotificationExpanded ? 'fas fa-chevron-down' : 'fas fa-chevron-up'
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.hasCoacheesWithoutCoach$ = this._userApplicationPort
			.observeSessionUser()
			.pipe(
				switchMap((user) =>
					combineLatest([user.organization$, user.partners$]).pipe(
						map(([organization, partners]) => ({
							organizations: [organization, ...partners],
							user,
						}))
					)
				),
				map((data) =>
					data.organizations.filter(
						(o) =>
							!o.isPropertyTrue(OrganizationPropertyName.IS_FICTITIOUS, false) &&
							data.user.hasRightOnOrganization(o.id, 'ORGANIZATION', 'CAN_CREATE')
					)
				),
				map((organizations) => organizations.map((o) => o.id)),
				switchMap((userOrganizationsId) =>
					this._userApplicationPort.observeCoacheesWithoutCoach(userOrganizationsId)
				),
				map((users) => users.data),
				map((coacheesWithoutCoach) => !isEmpty(coacheesWithoutCoach)),
				shareReplay({ refCount: true, bufferSize: 1 })
			);

		const sessionUser$ = this._userApplicationPort.observeSessionUser();

		const selectedUser$ = this.select('selectedUserId').pipe(
			switchMap((userId) =>
				userId
					? this._userApplicationPort
							.observeUserById(userId)
							.pipe(map((data) => data.data))
					: of(null as IUser)
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.appChatUserId$ = this._appService.appChatUserId$;

		const appSidebarSelectedUser$ =
			this._appService.appSidebarSelectedUserId$.pipe(
				switchMap((userId) =>
					userId
						? this._userApplicationPort
								.observeUserById(userId)
								.pipe(map((data) => data.data))
						: of(null as IUser)
				),
				shareReplay({ refCount: true, bufferSize: 1 })
			);

		this.coacheeName$ = combineLatest([
			selectedUser$,
			appSidebarSelectedUser$,
		]).pipe(
			map(
				([selectedUser, appSidebarSelectedUser]) =>
					(selectedUser || appSidebarSelectedUser)?.name
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.coacheeProfileRoute$ = this.select('selectedUserId').pipe(
			map((selectedUserId) => ['/coachee', selectedUserId]),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.userId$ = combineLatest([
			sessionUser$,
			selectedUser$,
			appSidebarSelectedUser$,
		]).pipe(
			map(
				([sessionUser, selectedUser, appSidebarSelectedUser]) =>
					(selectedUser || appSidebarSelectedUser || sessionUser)?.id
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);

		this.showCoachingPlan$ = combineLatest([
			sessionUser$,
			selectedUser$,
			appSidebarSelectedUser$,
		]).pipe(
			map(
				([sessionUser, selectedUser, appSidebarSelectedUser]) =>
					sessionUser?.isCoachee || !!selectedUser || !!appSidebarSelectedUser
			)
		);

		this.showCoacheesList$ = combineLatest([
			sessionUser$,
			selectedUser$,
			appSidebarSelectedUser$,
		]).pipe(
			map(
				([sessionUser, selectedUser, appSidebarSelectedUser]) =>
					sessionUser?.isCoach && !selectedUser && !appSidebarSelectedUser
			)
		);

		this.notifications$ = this._notificationApplicationPort
			.observeNotifications()
			.pipe(shareReplay({ bufferSize: 1, refCount: true }));

		this.showCloseAllButton$ = this.notifications$.pipe(
			map((notifications) =>
				notifications.map((notification) => notification.type)
			),
			map((notificationTypes) =>
				notificationTypes.some(
					(notificationType) =>
						notificationType !== NotificationType.MEETING_ALTERNATIVE &&
						notificationType !== NotificationType.MEETING_REQUEST
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.locale$ = this._appService.currentLang$;
	}
}
